// components
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Redux
import {
	retroClaimDetailsSelector,
	retroClaimIDSelector,
	retroClaimSpecialApprovalLoadingState,
	retroCreateClaimDetailsSelector,
	specialApprovalRetroClaim
} from "redux/retroClaim/retroClaimSlice";

// Type Definitions
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { UploadFile } from "antd/es/upload/interface";

// constants and utils
import { FILE_UPLOAD_CONSTANT, Routes, SPECIAL_APPROVAL_RETRO_CLAIM } from "constants/global";
import { getFormData } from "utils/getFormData";
import { getTotalUploadSize } from "utils/getTotalUploadSize";
import { disableFileUpload } from "utils/disableFileUpload";
import { validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";
import { uploadedFileTypeCheck } from "utils/uploadedFileTypeCheck";
import { getFileList } from "utils/getFileList";

export type InputValueType = {
	input: string;
	active: boolean;
};

export type LocationState = {
	state: {
		throughCreateClaim: boolean;
	};
};

const getSelector = (value: boolean) => {
	return value ? retroCreateClaimDetailsSelector : retroClaimDetailsSelector;
};

export function useSpecialApprovalRetroClaim() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { state } = location as LocationState;

	const retroClaimDetails = useAppSelector(getSelector(state?.throughCreateClaim));
	const retroClaimId = useAppSelector(retroClaimIDSelector);
	const loading = useAppSelector(retroClaimSpecialApprovalLoadingState) === 1;

	const [inputValue, setInputValue] = useState<InputValueType>({ input: "", active: false });
	const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
	const [isBtnClicked, setIsBtnClicked] = useState<boolean>(false);
	const [isReasonValid, setIsReasonValid] = useState(false);

	const goBack = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}`);
	}, [navigate, dispatch]);

	useEffect(() => {
		const { data } = retroClaimId;
		!data?.retro_claim_id && !isBtnClicked && goBack();
	}, [retroClaimId]);

	const validateReason = useCallback(
		(value: string) => {
			const isValidReason = validateRejectRetroClaimReason(value);
			setIsReasonValid(isValidReason);
		},
		[inputValue, isReasonValid]
	);

	const handleInput = useCallback(
		(value: string) => {
			if (value) {
				setInputValue({ ...inputValue, input: value, active: true });
				validateReason(value);
			} else {
				setInputValue({ input: "", active: false });
				validateReason(value);
			}
		},
		[inputValue, isReasonValid, validateReason, setInputValue]
	);

	const handleFileInput = useCallback(
		(info: any) => {
			const list = getFileList(info);
			setFileList(list);
			disableFileUpload(list?.length);
		},
		[fileList, setFileList]
	);

	const sendApprovalSuccess = useCallback(
		(hasError: boolean, message: string | undefined) => {
			if (hasError && message) {
				CountDownModal({
					timer: 3,
					title: SPECIAL_APPROVAL_RETRO_CLAIM.SUCCESS_MESSAGE,
					onTimeComplete: goBack
				});
			}
		},
		[CountDownModal, goBack]
	);

	const onFailure = useCallback(
		(err: any) => {
			if (err?.response?.data?.errors) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: SPECIAL_APPROVAL_RETRO_CLAIM.CANNOT_SEND_REQ,
					onTimeComplete: goBack
				});
			}
			if (err?.message) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: `${err?.message}${SPECIAL_APPROVAL_RETRO_CLAIM.API_ERROR}`
				});
			}
		},
		[CountDownModal]
	);

	const submitSpecialApproval = useCallback(async () => {
		if (inputValue.active) {
			const totalFileSize: number = await getTotalUploadSize(fileList);
			const isFileValid = await uploadedFileTypeCheck(SPECIAL_APPROVAL_RETRO_CLAIM.SPECIAL_APPROVAL_FILETYPE, fileList);
			if (fileList && (totalFileSize > 5 || !isFileValid)) {
				CountDownModal({
					timer: 3,
					title: !isFileValid ? FILE_UPLOAD_CONSTANT.UNSUPPORTED_FILE_TYPE : FILE_UPLOAD_CONSTANT.MAXIMUM_FILE_LIMIT,
					errorIcon: true
				});
			} else {
				const formdata = await getFormData({ fileList: fileList, retro_Claim_id: retroClaimId.data.retro_claim_id, remarks: inputValue.input });
				setIsBtnClicked(true);
				try {
					const { errors, message } = await dispatch(specialApprovalRetroClaim(formdata)).unwrap();
					sendApprovalSuccess(!errors?.length, message);
				} catch (error) {
					onFailure(error);
				}
			}
		}
	}, [goBack, fileList, inputValue, retroClaimId, getTotalUploadSize, onFailure, CountDownModal, uploadedFileTypeCheck]);

	return {
		retroClaimDetails,
		inputValue,
		isReasonValid,
		loading,
		handleInput,
		goBack,
		handleFileInput,
		submitSpecialApproval,
		getFormData,
		validateReason
	};
}
